import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Phaser from "phaser";
import placeholderUrl from "../../../@shared/assets/GameMaps/CityBG.png";
import {
  SPRITES_MAP,
  STICKERS_MAP,
  getAvatarFrameForDirection,
  scaleAvatarBodyOffset,
  scaleAvatarBodySize,
  getAvatarSpriteConfig,
  normalizeAvatarName,
} from "../../../@shared/utils/SpritesMap";
import {
  createOrbTextureKey,
  resolveAvatarData,
} from "../../../@shared/utils/orbAvatar";
import {
  preloadAvatars,
  preloadStickers,
  createAvatarAnimations,
  createWalkAnimations,
} from "../../../@shared/utils/SceneHelpers";
import ArenaScene from "./ArenaScene";
import VoidCityMerchant from "./VoidCityMerchant";
import DecksmithScene from "./DecksmithScene";
import GameHallScene from "./GameHallScene";
import Route333Scene from "../route333/Route333Scene";
import WhisperingForestScene from "../whisperingForest/WhisperingForestScene";
import VoidCity2Scene from "../voidcity2/VoidCity2Scene";
import {
  resolveStoryInitialSceneKey,
  resolveStoryInitialSceneSpawn,
} from "./storySceneRouting";
import NorthDocksScene from "../northDocks/NorthDocksScene";
import VoidCaveScene from "../voidCave/VoidCaveScene";
import VoidCaveDepthsScene from "../voidCave/VoidCaveDepthsScene";
import {
  DeepGalleryScene,
  RuneHollowScene,
  WestGrottoScene,
  WestPassageScene,
} from "../voidCave/westPassageScenes";
import {
  CrystalBasinScene,
  EastPassageScene,
  EchoVaultScene,
  ShatteredReachScene,
} from "../voidCave/eastPassageScenes";
import { VOID_CAVE_ENTRY_SPAWN } from "../voidCave/voidCaveMapConfig";
import { useAuthStore } from "../../../@shared/store/useAuthStore";
import { getVoidCityNpcDialogue } from "../../../@shared/utils/npcDialogs";
import NeoButton from "../../NeoButton";
import { MainContext } from "../../../App";
import { updatePlayerPresence } from "../../../@shared/utils/roomFunctions";
import QuestHallScene from "./QuestHallScene";
import AudioManager from "../../../@shared/utils/AudioManager";
import sticker from "../../../@shared/assets/badges/bronze_badge.png";
import fountain from "../../../@shared/assets/GameMaps/VoidCity/fountain.png";
import { startSceneWithTransition } from "../../../@shared/utils/startSceneWithTransition";
import { getBadgeByLevel } from "../../../@shared/utils/Utils";
import npcMap from "../../../@shared/assets/GameMaps/npcMap";
import { useStoryStore } from "../../../@shared/store/useStoryStore";
import {
  drawDebugBox,
  aabbFromPolygon,
  buildObstacleIndex,
  addStaticRect,
  addSensorRect,
  getDoorCenter,
} from "../../../@shared/utils/worldSceneHelpers";
import {
  ORB_FRAMES,
  ORB_GLASS,
  redrawOrbGlass,
  spawnWorldOrbs,
} from "../../../@shared/utils/orbHelpers";
import { getActiveMissionFromProgress } from "../../../@shared/utils/storyHelpers";
import { getVoidCityExteriorDoorSpawnByBuilding } from "../../../@shared/utils/voidCityDoorSpawns";
import {
  applyWorldSceneBackground,
  DEFAULT_WORLD_BACKGROUND_COLOR,
} from "../../../@shared/utils/worldSceneBackgrounds";
import DialogHUD from "../../StoryMode/DialogHUD";
import CardRewardModal from "../../CardRewardModal";
import { db } from "../../../@shared/firebase/config";
import { doc, getDoc, increment, setDoc, updateDoc } from "firebase/firestore";
import {
  buildWhisperingForestStoryPackGrant,
  isWhisperingForestStoryPackChestRequest,
  WHISPERING_FOREST_STORY_PACK_CHEST_MODE,
  WHISPERING_FOREST_STORY_PACK_REWARD_EVENT,
} from "../whisperingForest/storyPackChests";

const DOOR_SFX = require("../../../@shared/assets/sounds/door.mp3");
const WHISPERING_FOREST_TOSHI_REWARD = {
  id: "QW8eUlAKTTcLwX7nyKmY",
  imageStart:
    "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/NFT%20Images%2Ftoshi-start.png?alt=media&token=18f14ce4-66d6-4633-9a31-3461fce951b2",
  imageUrl:
    "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/NFT%20Images%2F546546.png?alt=media&token=b831e028-472a-45cc-8584-7e6e5e8f08e0",
  availableForPurchase: false,
  name: "Toshi",
  bgColor2: "#9ddacd",
  attackName: "Hydro Blade",
  collectionName: "Tadpoles",
  rarity: "Epic",
  health: "7",
  accuracy: "4",
  imageFin:
    "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/NFT%20Images%2Ftoshi-fin.png?alt=media&token=d8ff081d-3b8b-4a27-8ce8-86f60c785fdd",
  attack: "6",
  defense: "6",
  description:
    "Strong frontline presence, but needs to get in close—lower accuracy reflects a need to be strategic",
  tokenId:
    "57896044618658097711785492504343953926634992332820282019728792003956564827517",
  bgColor1: "#06dbb7",
  finite: false,
  price: "",
  season: "1",
  type: "character",
};

export const maps = {
  city: {
    background: require("../../../@shared/assets/GameMaps/CityBG.png"),
    collisions: require("../../../@shared/assets/GameMaps/void-city-collisions.json"),
    size: { width: 1627, height: 1024 },
    doors: {
      ArenaDoor: { to: "arena", spawnAt: { x: 292, y: 374 } },
      MerchantsRowDoor: { to: "merchant", spawnAt: { x: 260, y: 280 } },
      DecksmithDoor: { to: "decksmith", spawnAt: { x: 145, y: 164 } },
      QestHallDoor: { to: "questHall", spawnAt: { x: 698, y: 791 } },
    },
  },
  arena: {
    background: require("../../../@shared/assets/GameMaps/voidArena.png"),
    collisions: require("../../../@shared/assets/GameMaps/VoidCity/defaultArenaInterior.json"),
    size: { width: 706, height: 471 },
    doors: {
      exit: {
        to: "city",
        spawnAt: getVoidCityExteriorDoorSpawnByBuilding("arena"),
      },
    },
  },
  merchant: {
    background: require("../../../@shared/assets/GameMaps/voidMerchant.png"),
    collisions: require("../../../@shared/assets/GameMaps/voidMerchant.json"),
    size: { width: 595, height: 426 },
    doors: {
      exit: {
        to: "city",
        spawnAt: getVoidCityExteriorDoorSpawnByBuilding("merchant"),
      },
    },
  },
  decksmith: {
    background: require("../../../@shared/assets/GameMaps/voidDeckSmith.png"),
    collisions: require("../../../@shared/assets/GameMaps/voidDeckSmith.json"),
    size: { width: 373, height: 248 },
    doors: {
      exit: {
        to: "city",
        spawnAt: getVoidCityExteriorDoorSpawnByBuilding("decksmith"),
      },
    },
  },
  questHall: {
    background: require("../../../@shared/assets/GameMaps/voidQuestMaster.png"),
    collisions: require("../../../@shared/assets/GameMaps/voidQuestMaster.json"),
    size: { width: 706, height: 471 },
    doors: {
      exit: {
        to: "city",
        spawnAt: getVoidCityExteriorDoorSpawnByBuilding("questHall"),
      },
    },
  },

  // etc...
};

const ORB_RENDER_SIZE = 64;
const ORB_Y_OFFSET = 8;
const ORB_HOVER_DISTANCE = 3;
const ORB_HOVER_SPEED = 0.004;
const ORB_COLLISION_RADIUS = 12;
const DEBUG_VOID_CITY_COLLISIONS = false;
const isBuiltInOrbAvatarKey = (avatarKey = "", avatarImage = "") =>
  avatarKey === "io_default" ||
  avatarKey === "io" ||
  avatarKey.startsWith("io_") ||
  avatarKey === "infinity_orb" ||
  avatarKey === "infinty_orb" ||
  avatarKey.startsWith("i_o_") ||
  avatarKey.includes("orb") ||
  String(avatarImage || "").includes("InfintyOrbs");

const getOrbHoverOffset = (scene, display) => {
  if (!display) return 0;

  let hoverPhase = display.getData("hoverPhase");
  if (typeof hoverPhase !== "number") {
    hoverPhase = (((display.x || 0) * 0.013) + ((display.y || 0) * 0.017)) % (
      Math.PI * 2
    );
    display.setData("hoverPhase", hoverPhase);
  }

  const time = scene?.time?.now || 0;
  return Math.sin(time * ORB_HOVER_SPEED + hoverPhase) * ORB_HOVER_DISTANCE;
};

const syncOrbFacing = (display, { direction = null, vx = 0 } = {}) => {
  if (!display) return;
  if (direction === 270 || vx < 0) {
    display.setFlipX(true);
  } else if (direction === 90 || vx > 0) {
    display.setFlipX(false);
  }
};

const syncOrbDisplay = (scene, display, x, y, options = {}) => {
  if (!display) return;
  syncOrbFacing(display, options);
  display.setPosition(x, y - ORB_Y_OFFSET + getOrbHoverOffset(scene, display));
};

const destroyRemotePlayerVisual = (entry) => {
  entry?.sprite?.destroy?.();
  entry?.orbImage?.destroy?.();
  entry?.nameTag?.destroy?.();
  entry?.nameTagBg?.destroy?.();
  entry?.stickerImg?.destroy?.();
};

export default function VoidCityScene({
  setStartRoomVisible,
  setJoinRoomVisible,
  setSettingsVisible,
  setTownQuestsVisible,
  onLocalMove,
  players = {},
  sceneUserData = null,
  storyLaunchTarget = null,
}) {
  const containerRef = useRef(null);
  const { userData: authUserData } = useAuthStore();
  const userData = sceneUserData || authUserData;
  const {
    Theme,
    navigate,
    setMessage,
    setError,
    setScreenLoading,
    deviceType,
  } = useContext(MainContext);
  const storyLaunchKind = useStoryStore((s) => s.currentLaunchTarget?.kind);
  const storyLaunchNpc = useStoryStore((s) => s.currentLaunchTarget?.npc);
  const grantStoryInventoryItem = useStoryStore((s) => s.grantStoryInventoryItem);
  const isStoryCutsceneActive =
    userData?.activeGameMode === "story" && storyLaunchKind === "cutscene";
  const [currentMap, setCurrentMap] = useState("city");
  const [touchingNPC, setTouchingNPC] = useState(null);
  const [npcDialogue, setNpcDialogue] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogScript, setDialogScript] = useState([]);
  const [forestChestReward, setForestChestReward] = useState(null);
  const [forestChestModalVisible, setForestChestModalVisible] = useState(false);
  const openedForestChests = useMemo(
    () => userData?.openedChests?.whisperingForest || {},
    [userData?.openedChests?.whisperingForest],
  );

  const sceneRef = useRef(null); // ✅ 1. Create a ref to hold the Phaser scene
  const dialogOpenRef = useRef(false);
  const openingChestIdsRef = useRef(new Set());
  const openedForestChestsRef = useRef(openedForestChests);
  const pendingChestRewardsRef = useRef({});
  const pendingChestModalTimersRef = useRef({});

  const mapData = maps[currentMap];
  const initialStoryWorld = storyLaunchTarget?.world || null;
  const isStoryMode = userData?.activeGameMode === "story";
  const initialSceneKey = isStoryMode
    ? resolveStoryInitialSceneKey(initialStoryWorld)
    : "VoidCity2Scene";
  const initialScenePayload = useMemo(() => {
    if (!isStoryMode) {
      return {
        spawn: { x: 448, y: 1088 },
        returnSpawn: { x: 980, y: 560 },
        building: "",
      };
    }

    const world = storyLaunchTarget?.world || {};
    const spawn = resolveStoryInitialSceneSpawn(world);

    if (world?.building === "arena") {
      return {
        spawn,
        storySpawn: true,
        exit: maps.arena.doors.exit.spawnAt,
        building: "arena",
      };
    }

    return {
      spawn,
      storySpawn: true,
      building: world?.building || "",
      ...(initialSceneKey === "WhisperingForestScene"
        ? {
            forestChestMode: WHISPERING_FOREST_STORY_PACK_CHEST_MODE,
          }
        : {}),
    };
  }, [initialSceneKey, isStoryMode, storyLaunchTarget]);
  const initialSceneConfigRef = useRef({
    key: initialSceneKey,
    payload: initialScenePayload,
  });

  useEffect(() => {
    openedForestChestsRef.current = openedForestChests;
  }, [openedForestChests]);

  useEffect(() => {
    dialogOpenRef.current = dialogOpen;
    if (!dialogOpen && typeof window !== "undefined") {
      window.__npcEngaged = false;
    }
  }, [dialogOpen]);

  const { width: mapWidth, height: mapHeight } = mapData.size;

  const handleOptionKeyPress = (e) => {
    if (!npcDialogue) return;

    const keyMap = {
      1: 0,
      2: 1,
      3: 2,
    };

    const optionIndex = keyMap[e.key];

    if (optionIndex !== undefined) {
      const option = npcDialogue.options[optionIndex];
      if (!option) return;

      // console.log(`🔢 Key pressed: ${e.key}, triggering: ${option}`);

      if (
        ["Okay", "Nevermind", "Exit", "Got it", "Maybe Later"].includes(option)
      ) {
        setNpcDialogue(null);
      } else {
        setSelectedOption(option);
        setNpcDialogue(null);
      }
    }
  };

  useEffect(() => {
    const handleNpcProximity = (e) => {
      const npcName = e.detail;
      console.log("🧍‍♂️ NPC in range:", npcName);
      setTouchingNPC(npcName);
    };

    // const handleNpcDialogue = (e) => {
    //   const npcName = e.detail;

    //   // prevent unnecessary repeats
    //   if (npcDialogue || window.__npcEngaged) return;

    //   const dialogue = getVoidCityNpcDialogue(npcName);

    //   if (dialogue) {
    //     setNpcDialogue(dialogue);
    //   }
    // };

    const handleNpcDialogue = (e) => {
      if (typeof window !== "undefined" && window.__storyCutsceneActive) return;
      const payload = e.detail;
      const payloadNpcName =
        typeof payload === "string"
          ? payload
          : payload?.npc || payload?.id || payload?.name || null;
      const isStoryNpcDialogue =
        userData?.activeGameMode === "story" &&
        storyLaunchKind === "npc" &&
        storyLaunchNpc &&
        typeof payloadNpcName === "string" &&
        payloadNpcName.toLowerCase() === String(storyLaunchNpc).toLowerCase();
      if (isStoryNpcDialogue) return;
      if (dialogOpenRef.current) return;

      let nextScript = null;

      if (Array.isArray(payload)) {
        nextScript = payload;
      } else if (
        payload &&
        typeof payload === "object" &&
        (payload.text || payload.speaker || payload.choices)
      ) {
        nextScript = [payload];
      } else {
        const dialogue = getVoidCityNpcDialogue(payload);
        if (!dialogue) return;

        nextScript = [
          {
            ...dialogue,
            choices: (dialogue.options || []).map((label) => ({ label })),
            onChoose: ({ label }) => {
              if (
                ["Okay", "Nevermind", "Exit", "Got it", "Maybe Later"].includes(
                  label
                )
              )
                return;
              setSelectedOption(label);
            },
          },
        ];
      }

      if (!nextScript?.length) return;

      if (typeof window !== "undefined") {
        window.__npcEngaged = true;
      }
      setDialogScript(nextScript);
      setDialogOpen(true);
    };

    window.addEventListener("npc-proximity", handleNpcProximity);
    window.addEventListener("npc-dialogue", handleNpcDialogue);

    return () => {
      if (typeof window !== "undefined") {
        window.__npcEngaged = false;
      }
      window.removeEventListener("npc-proximity", handleNpcProximity);
      window.removeEventListener("npc-dialogue", handleNpcDialogue);
    };
  }, [storyLaunchKind, storyLaunchNpc, userData?.activeGameMode]);

  const setSelectedOption = (option) => {
    console.log(option);
    switch (option) {
      case "Create Room":
        if (isStoryMode) {
          setMessage("You cannot battle in the Arena in Story Mode yet.");
          setError(true);
          break;
        }
        if (userData?.decks?.length === 0) {
          setMessage("You need to create a deck first!");
          setError(true);
          return;
        }
        setStartRoomVisible(true);
        break;
      case "Join Room":
        if (isStoryMode) {
          setMessage("You cannot battle in the Arena in Story Mode yet.");
          setError(true);
          break;
        }
        setJoinRoomVisible(true);
        break;
      case "Craft a Deck":
        if (isStoryMode) {
          setMessage("You cannot craft a deck in Story Mode yet.");
          setError(true);
          break;
        }
        setScreenLoading(true);
        setTimeout(() => {
          navigate("/mycards");
        }, 500);
        break;
      case "Shop":
        if (userData?.activeGameMode === "story") {
          window.dispatchEvent(
            new CustomEvent("story-merchant-open", {
              detail: { mode: "buy" },
            }),
          );
          break;
        }
        setScreenLoading(true);
        setTimeout(() => {
          navigate("/store");
        }, 500);
        break;
      case "Sell":
        if (userData?.activeGameMode === "story") {
          window.dispatchEvent(
            new CustomEvent("story-merchant-open", {
              detail: { mode: "sell" },
            }),
          );
        }
        break;
      case "Choose Quests":
        setTownQuestsVisible(true);
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (!sceneRef.current) return;

    const selfId = userData.id;
    const sceneManager =
      sceneRef.current.scene?.keys
        ? sceneRef.current.scene
        : sceneRef.current.sys?.game?.scene?.keys
          ? sceneRef.current.sys.game.scene
          : null;

    if (!sceneManager) return;

    [
      "VillageScene",
      "ArenaScene",
      "MerchantScene",
      "DecksmithScene",
      "GameHallScene",
      "QuestHallScene",
      "Route333Scene",
      "WhisperingForestScene",
      "NorthDocksScene",
      "VoidCity2Scene",
    ].forEach((sceneKey) => {
      const scene = sceneManager.keys?.[sceneKey];
      if (scene) {
        scene.events.emit("setPlayers", players, selfId);
      }
    });
  }, [players]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    window.__voidCityOnLocalMove = onLocalMove;
    return () => {
      if (window.__voidCityOnLocalMove === onLocalMove) {
        delete window.__voidCityOnLocalMove;
      }
    };
  }, [onLocalMove]);

  useEffect(() => {
    const handleChestRequestOpen = async (event) => {
      const chestDetail = event.detail || {};
      const chestId = chestDetail?.chestId;
      if (!chestId || !userData?.id) return;
      if (openingChestIdsRef.current.has(chestId)) return;

      openingChestIdsRef.current.add(chestId);

      try {
        if (
          !isWhisperingForestStoryPackChestRequest(chestDetail) &&
          openedForestChests?.[chestId]
        ) {
          window.dispatchEvent(
            new CustomEvent("whispering-forest:chest-open-result", {
              detail: { chestId, ok: true },
            }),
          );
          return;
        }

        if (isWhisperingForestStoryPackChestRequest(chestDetail)) {
          const itemGrant = buildWhisperingForestStoryPackGrant(
            chestDetail.rewardCard,
          );
          if (!itemGrant) {
            throw new Error("Invalid Story pack chest reward.");
          }

          await grantStoryInventoryItem(userData.id, itemGrant);
          pendingChestRewardsRef.current[chestId] = {
            presentation: "story-pack",
            itemGrant,
            rewardLocation:
              String(chestDetail?.rewardLocation || "").trim() ||
              "Whispering Forest Chest",
          };

          window.dispatchEvent(
            new CustomEvent("whispering-forest:chest-open-result", {
              detail: { chestId, ok: true },
            }),
          );
          return;
        }

        await setDoc(
          doc(
            db,
            "users",
            userData.id,
            "openedChests",
            `whisperingForest__${chestId}`,
          ),
          {
            zone: "whisperingForest",
            chestId,
            openedAt: Date.now(),
            rewardType: "fixed",
            rewardCardId: WHISPERING_FOREST_TOSHI_REWARD.id,
          },
          { merge: true },
        );

        const userCardRef = doc(
          db,
          "users",
          userData.id,
          "Character cards",
          WHISPERING_FOREST_TOSHI_REWARD.id,
        );
        const ownedCardSnap = await getDoc(userCardRef);

        if (ownedCardSnap.exists()) {
          await updateDoc(userCardRef, {
            amountOwned: increment(1),
            new: true,
            lastUpdated: Date.now(),
          });
        } else {
          await setDoc(userCardRef, {
            ...WHISPERING_FOREST_TOSHI_REWARD,
            amountOwned: 1,
            createdAt: Date.now(),
            lastUpdated: Date.now(),
            new: true,
          });
        }

        pendingChestRewardsRef.current[chestId] = {
          id: `whispering-forest-${chestId}`,
          type: "character card",
          reward: WHISPERING_FOREST_TOSHI_REWARD,
          amount: 1,
        };

        if (pendingChestModalTimersRef.current[chestId]) {
          window.clearTimeout(pendingChestModalTimersRef.current[chestId]);
        }
        pendingChestModalTimersRef.current[chestId] = window.setTimeout(() => {
          const reward = pendingChestRewardsRef.current[chestId];
          if (!reward) return;
          setForestChestReward(reward);
          setForestChestModalVisible(true);
          delete pendingChestRewardsRef.current[chestId];
          delete pendingChestModalTimersRef.current[chestId];
        }, 300);

        window.dispatchEvent(
          new CustomEvent("whispering-forest:chest-open-result", {
            detail: { chestId, ok: true },
          }),
        );
      } catch (error) {
        console.error("Failed to open Whispering Forest chest:", error);
        setMessage("Chest reward failed. Please try again.");
        setError(true);
        window.dispatchEvent(
          new CustomEvent("whispering-forest:chest-open-result", {
            detail: { chestId, ok: false },
          }),
        );
      } finally {
        openingChestIdsRef.current.delete(chestId);
      }
    };

    window.addEventListener(
      "whispering-forest:chest-request-open",
      handleChestRequestOpen,
    );

    return () => {
      Object.values(pendingChestModalTimersRef.current).forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      pendingChestModalTimersRef.current = {};
      window.removeEventListener(
        "whispering-forest:chest-request-open",
        handleChestRequestOpen,
      );
    };
  }, [
    grantStoryInventoryItem,
    openedForestChests,
    setError,
    setMessage,
    userData?.id,
  ]);

  useEffect(() => {
    const handleChestAnimationComplete = (event) => {
      const chestId = event.detail?.chestId;
      if (!chestId) return;

      const reward = pendingChestRewardsRef.current[chestId];
      if (!reward) return;

      if (pendingChestModalTimersRef.current[chestId]) {
        window.clearTimeout(pendingChestModalTimersRef.current[chestId]);
        delete pendingChestModalTimersRef.current[chestId];
      }

      if (reward.presentation === "story-pack") {
        window.dispatchEvent(
          new CustomEvent(WHISPERING_FOREST_STORY_PACK_REWARD_EVENT, {
            detail: {
              itemGrant: reward.itemGrant,
              rewardLocation: reward.rewardLocation,
            },
          }),
        );
        delete pendingChestRewardsRef.current[chestId];
        return;
      }

      setForestChestReward(reward);
      setForestChestModalVisible(true);
      delete pendingChestRewardsRef.current[chestId];
    };

    window.addEventListener(
      "whispering-forest:chest-open-animation-complete",
      handleChestAnimationComplete,
    );

    return () => {
      Object.values(pendingChestModalTimersRef.current).forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      pendingChestModalTimersRef.current = {};
      window.removeEventListener(
        "whispering-forest:chest-open-animation-complete",
        handleChestAnimationComplete,
      );
    };
  }, []);

  useEffect(() => {
    console.log("🚀 Phaser scene mounting");
    const resolvedLocalAvatar = resolveAvatarData(userData);
    const avatarKey = normalizeAvatarName(
      userData?.avatar?.name || "io_default"
    );
    const heroUrl = SPRITES_MAP[avatarKey] || SPRITES_MAP["io_default"];
    const heroSpriteConfig = getAvatarSpriteConfig(avatarKey);
    const heroBodySize = scaleAvatarBodySize(avatarKey, {
      width: 64,
      height: 80,
    });
    const heroBodyOffset = scaleAvatarBodyOffset(avatarKey, {
      x: 32,
      y: 112,
    });
    const usesOrbCollisionBody =
      resolvedLocalAvatar.isOrbAvatar ||
      isBuiltInOrbAvatarKey(avatarKey, userData?.avatar?.image);
    const usesStaticOrbOverlay = !!resolvedLocalAvatar.usesStaticImageOverlay;
    const localOrbTextureKey = createOrbTextureKey(
      userData?.orbAvatar || { tokenId: "local-player" },
    );
    const orbDisplayTextureKey = usesStaticOrbOverlay
      ? localOrbTextureKey
      : "hero";
    let game;
    const initialSceneConfig = initialSceneConfigRef.current;
    const bootScenePayload = {
      ...initialSceneConfig.payload,
      userData,
      heroUrl,
      heroAvatarKey: avatarKey,
      storyLaunchTarget,
    };

    class StoryWorldBootScene extends Phaser.Scene {
      constructor() {
        super({ key: "StoryWorldBootScene" });
      }

      create() {
        this.scene.start(initialSceneConfig.key, bootScenePayload);
      }
    }

    class VillageScene extends Phaser.Scene {
      constructor() {
        super({ key: "VillageScene" });
      }

      preload() {
        const store = useStoryStore.getState();
        const progress = store.progress;
        const step = store.getCurrentStep?.(progress) || null;
        const acts = store.storyData?.acts || [];
        const mission = getActiveMissionFromProgress(acts, progress);

        const npcToRender = step?.npc || mission?.npc; // fallback to mission-level npc

        if (npcToRender && npcMap[npcToRender]) {
          const { key, url } = npcMap[npcToRender];
          if (!this.textures.exists(key)) {
            this.load.image(key, url);
            this.load.once("complete", () => {
              console.log("🖼️ Finished loading assets:", this.textures.list);
            });
          }
        }
        this.load.image("villageBg", placeholderUrl);
        this.load.spritesheet("hero", heroUrl, {
          frameWidth: heroSpriteConfig.frameWidth,
          frameHeight: heroSpriteConfig.frameHeight,
        });
        if (usesStaticOrbOverlay && resolvedLocalAvatar.image) {
          this.load.image(localOrbTextureKey, resolvedLocalAvatar.image);
        }
        this.load.spritesheet("fountain", fountain, {
          frameWidth: 241,
          frameHeight: 215,
        });

        this.load.image("default-sticker", sticker);
        preloadAvatars(this, SPRITES_MAP);
        preloadStickers(this, STICKERS_MAP);

        Object.values(npcMap).forEach((npc) => {
          if (this.textures.exists(npc.key)) return;

          if (npc.frameWidth && npc.frameHeight) {
            // spritesheet (Infinity Orbs)
            this.load.spritesheet(npc.key, npc.url, {
              frameWidth: npc.frameWidth,
              frameHeight: npc.frameHeight,
            });
          } else {
            // static image (e.g., Glyde)
            this.load.image(npc.key, npc.url);
          }
        });

        AudioManager.loadSFX("door", DOOR_SFX);
      }

      create() {
        sceneRef.current = this;
        applyWorldSceneBackground(this);
        const DEPTH = {
          BG: 0,
          NPC: 400,
          PLAYER: 500,
          UI: 510,
          FG: 1000,
          DEBUG: 9999,
        };

        const map = maps[currentMap];
        const bg = this.add.image(0, 0, "villageBg").setOrigin(0);
        this.cameras.main.setBounds(0, 0, bg.width, bg.height);
        this.scale.on("resize", () => {
          // if you ever switch scenes or resize during play, keep things stable
          // (camera follow will take over once player exists)
          if (!this.player) {
            this.cameras.main.centerOn(bg.width / 2, bg.height / 2);
          }
        });

        this.matter.world.setBounds(0, 0, bg.width, bg.height);
        if (DEBUG_VOID_CITY_COLLISIONS) {
          this.add
            .graphics()
            .setDepth(DEPTH.DEBUG)
            .lineStyle(4, 0xffe45c, 1)
            .strokeRect(0, 0, bg.width, bg.height);
        }

        this.anims.create({
          key: "fountainFlow",
          frames: this.anims.generateFrameNumbers("fountain", {
            start: 0,
            end: 3,
          }),
          frameRate: 4, // 8 frames per second
          repeat: -1, // loop forever
        });

        this.add.sprite(748, 423, "fountain").play("fountainFlow");

        // 🔁 build per-orb idle animations (safe because sheets are loaded in preload)
        ["npc_red_orb", "npc_blue_orb"].forEach((texKey) => {
          if (!this.anims.exists(`orb_idle_${texKey}`)) {
            this.anims.create({
              key: `orb_idle_${texKey}`,
              frames: this.anims.generateFrameNumbers(texKey, {
                start: 0,
                end: 3,
              }),
              frameRate: 6,
              repeat: -1,
            });
          }
        });

        this.lastDoorLabel = null;

        const fallbackStickerName = getBadgeByLevel(userData.level).name;
        const stickerName = userData?.sticker?.name || fallbackStickerName;
        const stickerKey = `sticker-${normalizeAvatarName(stickerName)}`;
        const stickerTextureExists = this.textures.exists(stickerKey);

        // console.log("🎯 Sticker name:", stickerName);
        // console.log("🔑 Sticker key:", stickerKey);
        // console.log("✅ Texture exists?", stickerTextureExists);

        // Phaser device test:
        const isMobile =
          this.sys.game.device.os.android || this.sys.game.device.os.iOS || deviceType === 'mobile';

        // pick zoom based on device
        const zoomLevel = isMobile ? 1.2 : 2.0;

        this.remotePlayers = {};
        this.latestPlayers = null;
        this.pendingOrbTextures = {};
        this.ensureOrbTexture = (orbAvatar) => {
          const avatarData = resolveAvatarData({ orbAvatar });
          if (!avatarData.isOrbAvatar || !avatarData.image) {
            return Promise.resolve("");
          }

          const textureKey = createOrbTextureKey(orbAvatar);
          if (this.textures.exists(textureKey)) {
            return Promise.resolve(textureKey);
          }
          if (this.pendingOrbTextures[textureKey]) {
            return this.pendingOrbTextures[textureKey];
          }

          this.pendingOrbTextures[textureKey] = new Promise((resolve, reject) => {
            const cleanup = () => {
              this.load.off(`filecomplete-image-${textureKey}`, handleComplete);
              this.load.off("loaderror", handleError);
            };
            const handleComplete = () => {
              cleanup();
              delete this.pendingOrbTextures[textureKey];
              if (this.latestPlayers) {
                this.playersFromReact = this.latestPlayers;
              }
              resolve(textureKey);
            };
            const handleError = (file) => {
              if (file?.key !== textureKey) return;
              cleanup();
              delete this.pendingOrbTextures[textureKey];
              reject(new Error(`Failed to load orb texture: ${textureKey}`));
            };

            this.load.once(`filecomplete-image-${textureKey}`, handleComplete);
            this.load.on("loaderror", handleError);
            this.load.image(textureKey, avatarData.image);
            this.load.start();
          });

          return this.pendingOrbTextures[textureKey];
        };
        this.joystick = { x: 0, y: 0 };
        const currentCollisions = map.collisions;

        // this.buildObstacleIndex(currentCollisions.buildings);
        this.obstacles = buildObstacleIndex(currentCollisions.buildings);

        // 🧱 Static Building Collisions

        currentCollisions.buildings
          .filter((b) => !b.name)
          .forEach((b) => {
            const rect = aabbFromPolygon(b.polygon);
            addStaticRect(this, rect);
            if (DEBUG_VOID_CITY_COLLISIONS) {
              drawDebugBox(
                this,
                rect.x,
                rect.y,
                rect.width,
                rect.height,
                0xff0000,
                0.15
              ).setDepth(DEPTH.DEBUG);
            }
          });

        // 🚪 Doors with Blue Debug

        const makeDoor = (id, label) => {
          const c = getDoorCenter(currentCollisions.buildings, id);
          if (!c) return;
          addSensorRect(
            this,
            { x: c.x, y: c.y, width: c.w, height: c.h },
            label
          );
          if (DEBUG_VOID_CITY_COLLISIONS) {
            drawDebugBox(this, c.x, c.y, c.w, c.h, 0x0000ff, 0.3).setDepth(
              DEPTH.DEBUG
            );
          }
        };
        makeDoor(42, "door_42");
        makeDoor(41, "door_41");
        makeDoor(43, "door_43");
        makeDoor(44, "door_44");
        makeDoor(45, "door_45"); //! uncomment out for door

        // 🧍 NPCs with Green Debug
        currentCollisions.buildings
          .filter(
            (b) =>
              b.name &&
              [
                "receptionist",
                "merchant",
                "decksmith",
                "questmaster",
                "questboard1",
                "questboard2",
                "voidcityfrog",
              ].includes(b.name)
          )
          .forEach((b) => {
            console.log("🧍 NPC:", b.name);
            const rect = aabbFromPolygon(b.polygon);
            addSensorRect(this, rect, `npc_${b.name}`);
            if (DEBUG_VOID_CITY_COLLISIONS) {
              drawDebugBox(
                this,
                rect.x,
                rect.y,
                rect.width,
                rect.height,
                0x00ff00,
                0.25
              ).setDepth(DEPTH.DEBUG);
            }
          });

        this.canInteract = false;
        this.currentDoor = null;
        this.currentDoorPos = new Phaser.Math.Vector2();

        console.log("👤 Creating player sprite");
        const spawn = this.scene.settings.data?.spawn || { x: 730, y: 560 };

        console.log(
          "📥 Received spawn in VillageScene:",
          this.scene.settings.data?.spawn
        );
        console.log("📍 Final spawn used in VillageScene:", spawn);

        if (usesOrbCollisionBody) {
          const orbBody = Phaser.Physics.Matter.Matter.Bodies.circle(
            spawn.x,
            spawn.y,
            ORB_COLLISION_RADIUS
          );
          this.player = this.matter.add.sprite(spawn.x, spawn.y, "hero", 0);
          this.player.setExistingBody(orbBody);
        } else {
          this.player = this.matter.add.sprite(spawn.x, spawn.y, "hero", 0);
          this.player.setBody(heroBodySize, heroBodyOffset);
        }
        this.player
          .setScale(heroSpriteConfig.worldScale || 1)
          .setFixedRotation();
        if (heroSpriteConfig.trimBottom) {
          this.player.setCrop(
            0,
            0,
            heroSpriteConfig.frameWidth,
            heroSpriteConfig.frameHeight - heroSpriteConfig.trimBottom
          );
        }
        if (usesOrbCollisionBody && this.player?.body?.bounds) {
          const bodyWidth =
            this.player.body.bounds.max.x - this.player.body.bounds.min.x;
          const bodyHeight =
            this.player.body.bounds.max.y - this.player.body.bounds.min.y;
          const targetDiameter = ORB_COLLISION_RADIUS * 2;
          const scaleX = targetDiameter / Math.max(bodyWidth, 1);
          const scaleY = targetDiameter / Math.max(bodyHeight, 1);

          Phaser.Physics.Matter.Matter.Body.scale(
            this.player.body,
            scaleX,
            scaleY
          );
          Phaser.Physics.Matter.Matter.Body.setPosition(this.player.body, {
            x: spawn.x,
            y: spawn.y,
          });
        }
        this.playerDebugGfx =
          DEBUG_VOID_CITY_COLLISIONS || heroSpriteConfig.showCollisionDebug
          ? this.add.graphics().setDepth(20000)
          : null;
        this.playerDebugLabel =
          DEBUG_VOID_CITY_COLLISIONS
            ? this.add
                .text(16, 16, "", {
                  font: "12px monospace",
                  fill: "#ffffff",
                  backgroundColor: "#000000aa",
                  padding: { x: 6, y: 4 },
                })
                .setDepth(20001)
                .setScrollFactor(0)
            : null;

        this.playerOrbImage = null;
        if (usesStaticOrbOverlay && this.textures.exists(orbDisplayTextureKey)) {
          this.player.setAlpha(0);
          this.playerOrbImage = this.add
            .image(spawn.x, spawn.y - ORB_Y_OFFSET, orbDisplayTextureKey)
            .setDisplaySize(ORB_RENDER_SIZE, ORB_RENDER_SIZE)
            .setDepth(DEPTH.PLAYER);
          syncOrbDisplay(this, this.playerOrbImage, spawn.x, spawn.y);
        }

        

        this.cameras.main
          .startFollow(this.player, true, 0.1, 0.1)
          .setZoom(zoomLevel);

        this.player.setDepth(DEPTH.PLAYER);

        addSensorRect(
          this,
          { x: 860, y: 560, width: 92, height: 92 },
          "door_46"
        );
        addSensorRect(
          this,
          { x: 980, y: 560, width: 116, height: 116 },
          "door_47"
        );
        addSensorRect(
          this,
          { x: 1100, y: 560, width: 116, height: 116 },
          "door_48"
        );
        addSensorRect(
          this,
          { x: 1220, y: 560, width: 116, height: 116 },
          "door_49"
        );

        const whisperingPortal = this.add.container(860, 560).setDepth(DEPTH.UI);
        const portalGlow = this.add.graphics();
        portalGlow.fillStyle(0x3cf18d, 0.18);
        portalGlow.fillCircle(0, 0, 52);
        portalGlow.fillStyle(0x68ffd1, 0.34);
        portalGlow.fillCircle(0, 0, 32);
        portalGlow.lineStyle(3, 0xc5fff0, 0.9);
        portalGlow.strokeCircle(0, 0, 20);

        const portalRune = this.add.text(0, -2, "WF", {
          font: "bold 18px Arial",
          fill: "#effff8",
          stroke: "#0b4c2e",
          strokeThickness: 4,
        }).setOrigin(0.5);

        const portalLabel = this.add.text(0, 42, "Whispering Forest", {
          font: "12px Arial",
          fill: "#e9fff3",
          stroke: "#123323",
          strokeThickness: 3,
        }).setOrigin(0.5);

        whisperingPortal.add([portalGlow, portalRune, portalLabel]);
        this.tweens.add({
          targets: whisperingPortal,
          y: 552,
          duration: 1400,
          ease: "Sine.inOut",
          yoyo: true,
          repeat: -1,
        });

        const voidCity2Portal = this.add.container(980, 560).setDepth(DEPTH.UI);
        const vc2Glow = this.add.graphics();
        vc2Glow.fillStyle(0x8fa2ff, 0.18);
        vc2Glow.fillCircle(0, 0, 52);
        vc2Glow.fillStyle(0xbac4ff, 0.34);
        vc2Glow.fillCircle(0, 0, 32);
        vc2Glow.lineStyle(3, 0xf2f4ff, 0.9);
        vc2Glow.strokeCircle(0, 0, 20);

        const vc2Rune = this.add.text(0, -2, "V2", {
          font: "bold 18px Arial",
          fill: "#f4f6ff",
          stroke: "#2d336d",
          strokeThickness: 4,
        }).setOrigin(0.5);

        const vc2Label = this.add.text(0, 42, "Void City 2", {
          font: "12px Arial",
          fill: "#f1f4ff",
          stroke: "#24295b",
          strokeThickness: 3,
        }).setOrigin(0.5);

        voidCity2Portal.add([vc2Glow, vc2Rune, vc2Label]);
        this.tweens.add({
          targets: voidCity2Portal,
          y: 552,
          duration: 1400,
          ease: "Sine.inOut",
          yoyo: true,
          repeat: -1,
        });

        const northDocksPortal = this.add.container(1100, 560).setDepth(DEPTH.UI);
        const ndGlow = this.add.graphics();
        ndGlow.fillStyle(0x67d7ff, 0.18);
        ndGlow.fillCircle(0, 0, 52);
        ndGlow.fillStyle(0x96e9ff, 0.34);
        ndGlow.fillCircle(0, 0, 32);
        ndGlow.lineStyle(3, 0xf0fbff, 0.9);
        ndGlow.strokeCircle(0, 0, 20);

        const ndRune = this.add.text(0, -2, "ND", {
          font: "bold 18px Arial",
          fill: "#f2fbff",
          stroke: "#13435b",
          strokeThickness: 4,
        }).setOrigin(0.5);

        const ndLabel = this.add.text(0, 42, "North Docks", {
          font: "12px Arial",
          fill: "#eefaff",
          stroke: "#15384c",
          strokeThickness: 3,
        }).setOrigin(0.5);

        northDocksPortal.add([ndGlow, ndRune, ndLabel]);
        this.tweens.add({
          targets: northDocksPortal,
          y: 552,
          duration: 1400,
          ease: "Sine.inOut",
          yoyo: true,
          repeat: -1,
        });

        const voidCavePortal = this.add.container(1220, 560).setDepth(DEPTH.UI);
        const caveGlow = this.add.graphics();
        caveGlow.fillStyle(0xd08bff, 0.18);
        caveGlow.fillCircle(0, 0, 52);
        caveGlow.fillStyle(0xe4b8ff, 0.34);
        caveGlow.fillCircle(0, 0, 32);
        caveGlow.lineStyle(3, 0xfff1ff, 0.9);
        caveGlow.strokeCircle(0, 0, 20);

        const caveRune = this.add.text(0, -2, "VC", {
          font: "bold 18px Arial",
          fill: "#fff4ff",
          stroke: "#5a2468",
          strokeThickness: 4,
        }).setOrigin(0.5);

        const caveLabel = this.add.text(0, 42, "Void Cave", {
          font: "12px Arial",
          fill: "#fff2ff",
          stroke: "#4c214f",
          strokeThickness: 3,
        }).setOrigin(0.5);

        voidCavePortal.add([caveGlow, caveRune, caveLabel]);
        this.tweens.add({
          targets: voidCavePortal,
          y: 552,
          duration: 1400,
          ease: "Sine.inOut",
          yoyo: true,
          repeat: -1,
        });

        // Spawn 3–4 orbs in the city
        this.worldOrbs = spawnWorldOrbs(
          this,
          npcMap,
          ["RedOrb", "BlueOrb"],
          4,
          DEPTH.NPC,
          {
            obstacles: this.obstacles,
            bounds: this.cameras.main.getBounds(),
            minClearance: 48,
            debug: DEBUG_VOID_CITY_COLLISIONS,
          }
        );

        const resolveNpcName = () => {
          const s = useStoryStore.getState();
          const progress = s.progress;
          const acts = s.storyData?.acts || [];
          const step = s.getCurrentStep?.(progress) || null;
          const mission = getActiveMissionFromProgress(acts, progress);
          return step?.npc || mission?.npc;
        };

        const spawnMissionNPC = () => {
          const npcName = resolveNpcName();
          if (!npcName || !npcMap[npcName]) return;

          const { key, url } = npcMap[npcName];
          if (!this.textures.exists(key)) this.load.image(key, url);
          this.load.once("complete", () => {
            // choose your placement (hard-coded or from collisions)
            const gx = 620,
              gy = 300;
            const cfg = npcMap[npcName];

            // ⬇️ NEW: read scale from npcMap, with per-map override fallback, then default
            const mapKey = currentMap; // e.g. "city", "arena"
            const scale =
              (cfg.scaleByMap && cfg.scaleByMap[mapKey]) ?? cfg.scale ?? 1;

            // this.add
            //   .image(gx, gy, key)
            //   .setOrigin(0.5, 1)
            //   .setDepth(400)
            //   .setScale(scale);

            const spr = this.add
              .image(gx, gy, key)
              .setOrigin(0.5, 1)
              .setDepth(400)
              .setScale(scale);

            // ⬇️ Make a small interaction box around his feet
            const dw = spr.displayWidth;
            const dh = spr.displayHeight;
            const sensorRect = {
              x: gx,
              y: gy - dh * 0.25, // just above the feet
              width: Math.max(48, dw * 0.6), // sensible footprint
              height: Math.max(32, dh * 0.3),
            };
            addSensorRect(
              this,
              sensorRect,
              `npc_${(npcName || "glyde").toLowerCase()}`
            );
            if (DEBUG_VOID_CITY_COLLISIONS) {
              drawDebugBox(
                this,
                sensorRect.x,
                sensorRect.y,
                sensorRect.width,
                sensorRect.height,
                0x00ff00,
                0.2
              ).setDepth(DEPTH.DEBUG);
            }

            console.log("✅ Spawned mission NPC:", npcName);
          });
          this.load.start();
        };

        // try once immediately (in case progress already there)
        spawnMissionNPC();

        // subscribe once; fire whenever progress arrives/changes
        this._unsubStory = useStoryStore.subscribe(
          (s) => s.progress,
          () => spawnMissionNPC()
        );

        this.events.once("shutdown", () => {
          this._unsubStory?.();
          this._unsubStory = null;
        });

        // Create background box
        this.playerNameBg = this.add.graphics();

        this.playerStickerImg = this.add
          .image(
            this.player.x,
            this.player.y - 70,
            stickerTextureExists ? stickerKey : "default-sticker"
          )
          .setScale(0.075)
          .setOrigin(0.5, 1);

        this.playerName = this.add
          .text(this.player.x, this.player.y - 100, "Loading...", {
            font: "12px Arial",
            fill: "#ffffff",
            stroke: "#000000",
            strokeThickness: 2,
          })
          .setOrigin(0.5, 1); // Center horizontally, align bottom vertically

        createAvatarAnimations(this);

        this.playerNameBg.setDepth(DEPTH.UI);
        this.playerName.setDepth(DEPTH.UI);
        this.playerStickerImg.setDepth(DEPTH.UI);

        this.matter.world.on("collisionstart", (ev) => {
          ev.pairs.forEach(({ bodyA, bodyB }) => {
            const sensor = bodyA.isSensor
              ? bodyA
              : bodyB.isSensor
                ? bodyB
                : null;
            const isPlayer =
              bodyA === this.player.body || bodyB === this.player.body;

            if (sensor && isPlayer) {
              const label = sensor.label;

              if (label?.startsWith("door_")) {
                this.canInteract = true;
                this.currentDoor = label;
                this.currentDoorPos.copy(sensor.position);
                window.dispatchEvent(
                  new CustomEvent("interact-proximity", { detail: true })
                );
                const doorNames = {
                  door_45: "Route 333",
                  door_46: "Whispering Forest",
                  door_47: "Void City 2",
                  door_48: "North Docks",
                  door_49: "Void Cave",
                };
                window.dispatchEvent(
                  new CustomEvent("door-name", {
                    detail: doorNames[label] || label,
                  })
                );
              } else if (label?.startsWith("npc_")) {
                const npcName = label.replace("npc_", "");
                console.log("🧍 NPC proximity detected:", npcName);
                this.canInteract = true;
                this.currentNPC = npcName;
                window.dispatchEvent(
                  new CustomEvent("npc-proximity", { detail: npcName })
                );
              }
            }
          });
        });

        // after your existing collisionstart handler setup in create():
        this.matter.world.on("collisionstart", (ev) => {
          ev.pairs.forEach(({ bodyA, bodyB }) => {
            const isPlayerA = bodyA === this.player?.body;
            const isPlayerB = bodyB === this.player?.body;
            if (!isPlayerA && !isPlayerB) return;

            // Check if the other body belongs to one of our orbs
            const other = isPlayerA ? bodyB : bodyA;
            const hitOrb = this.worldOrbs?.find((o) => o.body === other);
            if (!hitOrb) return;

            // 1) stop both immediately
            this.player.setVelocity(0, 0);
            hitOrb.setVelocity(0, 0);

            // 2) optional: gate input while touching (prevents "pushing")
            this.playerMovementBlocked = true;

            // 3) optional: tiny separation nudge away from each other
            const dx = this.player.x - hitOrb.x;
            const dy = this.player.y - hitOrb.y;
            const len = Math.hypot(dx, dy) || 1e-9;
            const nx = dx / len,
              ny = dy / len;
            const sep = 0.5; // small separation speed
            this.player.setVelocity(nx * sep, ny * sep);
            hitOrb.setVelocity(-nx * sep, -ny * sep);
          });
        });

        // Also release the input gate when they separate
        this.matter.world.on("collisionend", (ev) => {
          ev.pairs.forEach(({ bodyA, bodyB }) => {
            const aPlayer = bodyA === this.player?.body;
            const bPlayer = bodyB === this.player?.body;
            if (!aPlayer && !bPlayer) return;

            const other = aPlayer ? bodyB : bodyA;
            const wasOrb = this.worldOrbs?.some((o) => o.body === other);
            if (!wasOrb) return;

            this.playerMovementBlocked = false;
          });
        });

        this.matter.world.on("collisionend", (ev) => {
          ev.pairs.forEach(({ bodyA, bodyB }) => {
            const sensor = bodyA.isSensor
              ? bodyA
              : bodyB.isSensor
                ? bodyB
                : null;
            const isPlayer =
              bodyA === this.player.body || bodyB === this.player.body;

            if (sensor && isPlayer) {
              const label = sensor.label;

              if (label?.startsWith("door_")) {
                if (this.currentDoor === label) {
                  this.canInteract = false;
                  this.currentDoor = null;
                  window.dispatchEvent(
                    new CustomEvent("interact-proximity", { detail: false })
                  );
                  window.dispatchEvent(
                    new CustomEvent("door-name", { detail: null })
                  );
                }
              } else if (!label?.startsWith("npc_")) {
                console.log("👋 Left NPC area:", label.replace("npc_", ""));
                this.canInteract = false;
                this.currentNPC = null;
                window.dispatchEvent(
                  new CustomEvent("npc-proximity", { detail: null })
                );
              }
            }
          });
        });

        this.lastBroadcast = {
          x: null,
          y: null,
          direction: null,
          animState: null,
        };

        createWalkAnimations(this, "hero", avatarKey);
        this.cursors = this.input.keyboard.createCursorKeys();
        this.keys = this.input.keyboard.addKeys({
          W: Phaser.Input.Keyboard.KeyCodes.W,
          A: Phaser.Input.Keyboard.KeyCodes.A,
          S: Phaser.Input.Keyboard.KeyCodes.S,
          D: Phaser.Input.Keyboard.KeyCodes.D,
        });

        this.spaceKey = this.input.keyboard.addKey(
          Phaser.Input.Keyboard.KeyCodes.SPACE
        );
        this.spaceKey.on("down", () => {
          if (typeof this._onEnter === "function") {
            this._onEnter();
          }
        });

        this.playersFromReact = null;

        this.events.on("setPlayers", (players, selfId) => {
          // console.log("🌍 Received players update from React", players);
          this.latestPlayers = { players, selfId };
          this.playersFromReact = { players, selfId };
          // If player sprite is already created, assign name
          if (this.player) {
            const name = players?.[selfId]?.name || "You";
            // const level = players?.[selfId]?.level ?? 1;
            this.playerName?.setText(`${name}`);
          }
        });

        // Let the React ⚡ button behave like SPACE
        this._onPressEnter = () => {
          this._onEnter && this._onEnter();
        };
        window.addEventListener("press-enter", this._onPressEnter);

        this._onEnter = () => {
          if (typeof window !== "undefined" && window.__storyCutsceneActive) {
            return;
          }
          console.log("🌍 onEnter", this);
          console.log("Players", this?.playersFromReact);
          // let player = players[userData.id];
          if (this.canInteract && this.currentDoor) {
            if (this.currentDoor === "door_42") {
              const exitPolygon = maps.decksmith.collisions.buildings.find(
                (b) => b.name === "exit"
              )?.polygon;

              let spawn = { x: 300, y: 300 }; // fallback

              if (exitPolygon) {
                const xs = exitPolygon.map(([x]) => x);
                const ys = exitPolygon.map(([, y]) => y);
                spawn = {
                  x: (Math.min(...xs) + Math.max(...xs)) / 2,
                  y: (Math.min(...ys) + Math.max(...ys)) / 2 - 30,
                };
              }
              AudioManager.playSFX("door");
              this.cameras.main.fadeOut(300, 0, 0, 0);
              this.cameras.main.once("camerafadeoutcomplete", () => {
                this.scene.start("ArenaScene", {
                  spawn,
                  exit: maps.arena.doors.exit.spawnAt,
                  userData, // ✅ passing it here
                  heroUrl,
                  heroAvatarKey: avatarKey,
                  building: "arena",
                  allData: this,
                });
              });
            } else if (this.currentDoor === "door_41") {
              const exitPolygon = maps.decksmith.collisions.buildings.find(
                (b) => b.name === "exit"
              )?.polygon;

              let spawn = { x: 300, y: 300 }; // fallback

              if (exitPolygon) {
                const xs = exitPolygon.map(([x]) => x);
                const ys = exitPolygon.map(([, y]) => y);
                spawn = {
                  x: (Math.min(...xs) + Math.max(...xs)) / 2,
                  y: (Math.min(...ys) + Math.max(...ys)) / 2 - 30,
                };
              }
              AudioManager.playSFX("door");
              this.cameras.main.fadeOut(300, 0, 0, 0);
              this.cameras.main.once("camerafadeoutcomplete", () => {
                this.scene.start("MerchantScene", {
                  spawn,
                  exit: maps.merchant.doors.exit.spawnAt,
                  userData, // ✅ passing it here
                  heroUrl,
                  heroAvatarKey: avatarKey,
                  building: "merchant",
                  allData: this,
                });
              });
            } else if (this.currentDoor === "door_43") {
              const exitPolygon = maps.decksmith.collisions.buildings.find(
                (b) => b.name === "exit"
              )?.polygon;

              let spawn = { x: 300, y: 300 }; // fallback

              if (exitPolygon) {
                const xs = exitPolygon.map(([x]) => x);
                const ys = exitPolygon.map(([, y]) => y);
                spawn = {
                  x: (Math.min(...xs) + Math.max(...xs)) / 2,
                  y: (Math.min(...ys) + Math.max(...ys)) / 2 - 30,
                };
              }
              AudioManager.playSFX("door");
              this.cameras.main.fadeOut(300, 0, 0, 0);
              this.cameras.main.once("camerafadeoutcomplete", () => {
                this.scene.start("DecksmithScene", {
                  spawn,
                  exit: maps.decksmith.doors.exit.spawnAt,
                  userData, // ✅ passing it here
                  heroUrl,
                  heroAvatarKey: avatarKey,
                  building: "decksmith",
                });
              });
            } else if (this.currentDoor === "door_44") {
              const exitPolygon = maps.decksmith.collisions.buildings.find(
                (b) => b.name === "exit"
              )?.polygon;

              let spawn = { x: 300, y: 300 }; // fallback

              if (exitPolygon) {
                const xs = exitPolygon.map(([x]) => x);
                const ys = exitPolygon.map(([, y]) => y);
                spawn = {
                  x: (Math.min(...xs) + Math.max(...xs)) / 2,
                  y: (Math.min(...ys) + Math.max(...ys)) / 2 - 30,
                };
              }
              AudioManager.playSFX("door");
              this.cameras.main.fadeOut(300, 0, 0, 0);
              this.cameras.main.once("camerafadeoutcomplete", () => {
                this.scene.start("QuestHallScene", {
                  spawn,
                  exit: maps.questHall.doors.exit.spawnAt,
                  userData, // ✅ passing it here
                  heroUrl,
                  heroAvatarKey: avatarKey,
                  storyLaunchTarget,
                  building: "questHall",
                });
              });
            } else if (this.currentDoor === "door_45") {
              AudioManager.playSFX("door");
              this.cameras.main.fadeOut(300, 0, 0, 0);
              this.cameras.main.once("camerafadeoutcomplete", () => {
                this.scene.start("Route333Scene", {
                  // optional: pass a nicer spawn if you like
                  spawn,
                  heroUrl,
                  heroAvatarKey: avatarKey,
                  userData,
                });
              });
            } else if (this.currentDoor === "door_46") {
              AudioManager.playSFX("door");
              this.cameras.main.fadeOut(300, 0, 0, 0);
              this.cameras.main.once("camerafadeoutcomplete", () => {
                this.scene.start("WhisperingForestScene", {
                  returnSpawn: { x: 820, y: 560 },
                  heroUrl,
                  heroAvatarKey: avatarKey,
                  userData,
                  openedChests: openedForestChestsRef.current,
                  ...(userData?.activeGameMode === "story"
                    ? {
                        forestChestMode:
                          WHISPERING_FOREST_STORY_PACK_CHEST_MODE,
                      }
                    : {}),
                });
              });
            } else if (this.currentDoor === "door_47") {
              AudioManager.playSFX("door");
              this.cameras.main.fadeOut(300, 0, 0, 0);
              this.cameras.main.once("camerafadeoutcomplete", () => {
                this.scene.start("VoidCity2Scene", {
                  spawn: { x: 448, y: 1088 },
                  returnSpawn: { x: 980, y: 560 },
                  heroUrl,
                  heroAvatarKey: avatarKey,
                  userData,
                });
              });
            } else if (this.currentDoor === "door_48") {
              AudioManager.playSFX("door");
              this.cameras.main.fadeOut(300, 0, 0, 0);
              this.cameras.main.once("camerafadeoutcomplete", () => {
                this.scene.start("NorthDocksScene", {
                  spawn: { x: 480, y: 520 },
                  returnSpawn: { x: 1100, y: 560 },
                  heroUrl,
                  heroAvatarKey: avatarKey,
                  userData,
                });
              });
            } else if (this.currentDoor === "door_49") {
              AudioManager.playSFX("door");
              this.cameras.main.fadeOut(300, 0, 0, 0);
              this.cameras.main.once("camerafadeoutcomplete", () => {
                this.scene.start("VoidCaveScene", {
                  spawn: VOID_CAVE_ENTRY_SPAWN,
                  returnSpawn: { x: 1220, y: 560 },
                  heroUrl,
                  heroAvatarKey: avatarKey,
                  userData,
                });
              });
            } else if (this.canInteract && this.currentNPC) {
              window.dispatchEvent(
                new CustomEvent("npc-dialogue", {
                  detail: this.currentNPC,
                })
              );
            } else if (this.currentNPC) {
              window.dispatchEvent(
                new CustomEvent("npc-dialogue", { detail: this.currentNPC })
              );
            }
          }
        };
        console.log(
          "Enter pressed. canInteract:",
          this.canInteract,
          "currentNPC:",
          this.currentNPC
        );

        window.addEventListener("press-enter", this._onEnter);

        this.events.once("shutdown", () => {
          window.removeEventListener("press-enter", this._onEnter);
          this.spaceKey?.off?.("down"); // Clean up to avoid memory leaks

          this.currentNPC = null;
          this.currentDoor = null;
          if (typeof window !== "undefined") {
            window.__npcEngaged = false;
            window.dispatchEvent(
              new CustomEvent("npc-proximity", { detail: null })
            );
            window.dispatchEvent(
              new CustomEvent("interact-proximity", { detail: false })
            );
          }
        });
      }

      update() {
        const speed = 2;
        let vx = 0,
          vy = 0;
        const storyMovementLocked =
          typeof window !== "undefined" &&
          userData?.activeGameMode === "story" &&
          (window.__storyCutsceneActive || window.__npcEngaged);

        const { x: jx, y: jy } = this.joystick;
        if (jx || jy) {
          vx = jx * speed;
          vy = jy * speed;
        } else {
          if (this.cursors.left.isDown || this.keys.A.isDown) {
            vx = -speed;
          } else if (this.cursors.right.isDown || this.keys.D.isDown) {
            vx = speed;
          }
          if (this.cursors.up.isDown || this.keys.W.isDown) {
            vy = -speed;
          } else if (this.cursors.down.isDown || this.keys.S.isDown) {
            vy = speed;
          }
        }

        if (vx && vy) {
          if (Math.abs(vx) >= Math.abs(vy)) {
            vy = 0;
          } else {
            vx = 0;
          }
        }

        if (this.playerMovementBlocked || storyMovementLocked) {
          vx = 0;
          vy = 0;
          // stop the walk anim so the player visually halts
          this.player.anims.stop();
          // (don’t return; we still want the rest of update() to run)
        }

        this.player.setVelocity(vx, vy);

        if (usesOrbCollisionBody && this.player?.body?.bounds) {
          const bodyWidth =
            this.player.body.bounds.max.x - this.player.body.bounds.min.x;
          const bodyHeight =
            this.player.body.bounds.max.y - this.player.body.bounds.min.y;
          const targetDiameter = ORB_COLLISION_RADIUS * 2;

          if (
            Math.abs(bodyWidth - targetDiameter) > 1 ||
            Math.abs(bodyHeight - targetDiameter) > 1
          ) {
            Phaser.Physics.Matter.Matter.Body.scale(
              this.player.body,
              targetDiameter / Math.max(bodyWidth, 1),
              targetDiameter / Math.max(bodyHeight, 1)
            );
            Phaser.Physics.Matter.Matter.Body.setPosition(this.player.body, {
              x: this.player.x,
              y: this.player.y,
            });
          }
        }

        if (this.playerDebugGfx && this.player?.body?.bounds) {
          const bounds = this.player.body.bounds;
          const width = bounds.max.x - bounds.min.x;
          const height = bounds.max.y - bounds.min.y;
          const debugColor = usesOrbCollisionBody ? 0xff4fd8 : 0x7bf1a8;

          this.playerDebugGfx.clear();
          this.playerDebugGfx.lineStyle(2, debugColor, 0.95);
          this.playerDebugGfx.fillStyle(debugColor, 0.12);
          this.playerDebugGfx.strokeRect(bounds.min.x, bounds.min.y, width, height);
          this.playerDebugGfx.fillRect(bounds.min.x, bounds.min.y, width, height);

          if (this.playerDebugLabel) {
            this.playerDebugLabel.setText(
              [
                `avatarKey=${avatarKey}`,
                `orbPath=${usesOrbCollisionBody ? "yes" : "no"}`,
                `body=${width.toFixed(1)}x${height.toFixed(1)}`,
                `target=${ORB_COLLISION_RADIUS * 2}`,
              ].join("  ")
            );
          }
        }

        if (usesStaticOrbOverlay) {
          this.player.anims.stop();
          this.player.setAlpha(0);
          syncOrbDisplay(this, this.playerOrbImage, this.player.x, this.player.y, {
            vx,
          });
        } else if (Math.abs(vy) > Math.abs(vx)) {
          if (vy < 0) this.player.anims.play("walk-up", true);
          else if (vy > 0) this.player.anims.play("walk-down", true);
        } else if (vx < 0) {
          this.player.anims.play("walk-left", true);
        } else if (vx > 0) {
          this.player.anims.play("walk-right", true);
        } else {
          this.player.anims.stop();
        }

        if (this.player && this.playerName && this.playerNameBg) {
          const x = this.player.x;
          const y = this.player.y - 50;

          this.playerName.setPosition(x + 2, y);
          this.playerStickerImg?.setPosition(
            this.player.x - 40,
            this.player.y - 42
          );

          const padding = 6;
          const textWidth = this.playerName.width;
          const textHeight = this.playerName.height;

          this.playerNameBg.clear();
          this.playerNameBg.fillStyle(0x000000, 0.5); // Black with 50% opacity
          this.playerNameBg.fillRoundedRect(
            x - textWidth / 2 - padding,
            y - textHeight - padding,
            textWidth + padding * 2,
            textHeight + padding * 2,
            8 // corner radius
          );
        }

        if (this.lastDoorLabel !== this.currentDoor) {
          this.lastDoorLabel = this.currentDoor;
          window.dispatchEvent(
            new CustomEvent("door-name", { detail: this.currentDoor })
          );
        }

        if (this.playersFromReact) {
          // console.log("playersFromReact", this.playersFromReact);
          const { players, selfId } = this.playersFromReact;
          const currentPlayer = players[userData.id];

          if (!currentPlayer) return; // Prevent null logic

          this.playersFromReact = null;

          Object.entries(players).forEach(([uid, p]) => {
            if (
              !p ||
              uid === userData.id ||
              !p.x ||
              !p.y ||
              p.world !== currentPlayer?.world ||
              p.building !== currentPlayer?.building
            ) {
              return;
            }

            const existing = this.remotePlayers[uid];
            const avatarData = resolveAvatarData(p);
            const spriteKey = avatarData.isOrbAvatar
              ? createOrbTextureKey(p.orbAvatar)
              : `avatar-${normalizeAvatarName(p?.avatar?.name || "io_default")}`;

            if (!existing) {
              console.log("➕ Adding remote player sprite", uid);
              if (avatarData.isOrbAvatar && !this.textures.exists(spriteKey)) {
                this.ensureOrbTexture(p.orbAvatar).catch((error) => {
                  console.warn("Failed to load remote orb avatar texture:", error);
                });
                return;
              }

              const sprite = avatarData.isOrbAvatar
                ? this.add.sprite(p.x, p.y, "avatar-io_default", 0).setAlpha(0)
                : this.add.sprite(p.x, p.y, spriteKey, 0).setAlpha(0.8);

              if (sprite) {
                sprite.setScale(this.player.scaleX, this.player.scaleY);
              }

              const orbImage =
                avatarData.isOrbAvatar && this.textures.exists(spriteKey)
                  ? this.add
                      .image(p.x, p.y - ORB_Y_OFFSET, spriteKey)
                      .setDisplaySize(ORB_RENDER_SIZE, ORB_RENDER_SIZE)
                      .setAlpha(0.92)
                  : null;
              syncOrbDisplay(this, orbImage, p.x, p.y, {
                direction: p.direction,
              });

              const nameTagBg = this.add.graphics();
              const level = p.level ?? 1;

              const nameTag = this.add
                .text(p.x + 8, p.y - 50, `${p.name || "Unknown"}`, {
                  // .text(p.x + 8, p.y - 50, `Lv${level} ${p.name || "Unknown"}`, {
                  font: "12px Arial",
                  fill: "#ffffff",
                  stroke: "#000000",
                  strokeThickness: 3,
                })
                .setOrigin(0.5, 1); // Centered horizontally

              const stickerName =
                p?.sticker || getBadgeByLevel(p?.level || 1).name;
              const stickerKey = `sticker-${normalizeAvatarName(stickerName)}`;
              const stickerTextureExists = this.textures.exists(stickerKey);

              const stickerImg = this.add
                .image(
                  p.x,
                  p.y - 70,
                  stickerTextureExists ? stickerKey : "default-sticker"
                )
                .setScale(0.075)
                .setOrigin(0.5, 1);

              const padding = 6;
              const textWidth = nameTag.width;
              const textHeight = nameTag.height;

              nameTagBg.fillStyle(0x000000, 0.5);
              nameTagBg.fillRoundedRect(
                p.x - textWidth / 2 - padding + 8, // shift right to match text
                p.y - 50 - textHeight - padding,
                textWidth + padding * 2,
                textHeight + padding * 2,
                8
              );

              this.remotePlayers[uid] = {
                sprite,
                orbImage,
                nameTag,
                nameTagBg,
                stickerImg,
              };
            } else {
              const level = p.level ?? 1;
              if (
                avatarData.isOrbAvatar &&
                !existing.orbImage &&
                this.textures.exists(spriteKey)
              ) {
                existing.orbImage = this.add
                  .image(p.x, p.y - ORB_Y_OFFSET, spriteKey)
                  .setDisplaySize(ORB_RENDER_SIZE, ORB_RENDER_SIZE)
                  .setAlpha(0.92);
                syncOrbDisplay(this, existing.orbImage, p.x, p.y, {
                  direction: p.direction,
                });
              }
              existing.sprite?.setPosition(p.x, p.y);
              syncOrbDisplay(this, existing.orbImage, p.x, p.y, {
                direction: p.direction,
              });
              existing.nameTag.setText(`${p.name || "Unknown"}`);
              //   existing.nameTag.setText(`Lv${level} ${p.name || "Unknown"}`);
              existing.nameTag.setOrigin(0.5, 1);
              existing.nameTag.setPosition(p.x + 8, p.y - 50);

              const padding = 6;
              const textWidth = existing.nameTag.width;
              const textHeight = existing.nameTag.height;

              existing.nameTagBg.clear();
              existing.nameTagBg.fillStyle(0x000000, 0.5);
              existing.nameTagBg.fillRoundedRect(
                p.x - textWidth / 2 - padding + 8,
                p.y - 50 - textHeight - padding,
                textWidth + padding * 2,
                textHeight + padding * 2,
                8
              );

              existing.stickerImg?.setPosition(p.x - 40, p.y - 42);
            }

            const frameOffset =
              p.animState === "walking" ? ((Date.now() % 400) / 100) | 0 : 0;
            if (this.remotePlayers[uid].sprite) {
              this.remotePlayers[uid].sprite.setFrame(
                getAvatarFrameForDirection(
                  p?.avatar?.name || "io_default",
                  p.direction,
                  frameOffset,
                ),
              );
            }
            syncOrbDisplay(this, this.remotePlayers[uid].orbImage, p.x, p.y, {
              direction: p.direction,
            });
          });

          // Clean up removed players
          Object.keys(this.remotePlayers).forEach((uid) => {
            const p = players[uid];
            if (
              !p ||
              uid === selfId ||
              p.world !== currentPlayer.world ||
              p.building !== currentPlayer.building
              ) {
                console.log("❌ Removing remote player sprite", uid);
                destroyRemotePlayerVisual(this.remotePlayers[uid]);
                delete this.remotePlayers[uid];
              }
            });
        }

        if (onLocalMove) {
          const dir =
            vx < 0
              ? 270 // left
              : vx > 0
                ? 90 // right
                : vy < 0
                  ? 180 // up
                  : vy > 0
                    ? 0 // down
                    : null;

          const animState = vx === 0 && vy === 0 ? "idle" : "walking";

          const shouldBroadcast =
            this.player.x !== this.lastBroadcast.x ||
            this.player.y !== this.lastBroadcast.y ||
            dir !== this.lastBroadcast.direction ||
            animState !== this.lastBroadcast.animState;

          if (shouldBroadcast) {
            this.lastBroadcast = {
              x: this.player.x,
              y: this.player.y,
              direction: dir,
              animState,
            };
            onLocalMove(this.player.x, this.player.y, dir ?? 0, animState);
          }
        }
        if (this.worldOrbs) {
          for (const orb of this.worldOrbs) {
            if (orb._dbg) {
              const { boxG, circG, r } = orb._dbg;
              // 1) Sprite bounds (green translucent box)
              const b = orb.getBounds(); // respects scale + origin
              boxG.clear();
              boxG.fillStyle(0x00ff00, 0.25);
              boxG.fillRect(b.x, b.y, b.width, b.height);

              // 2) Matter body circle (magenta)
              circG.clear();
              circG.lineStyle(2, 0xff00ff, 1);
              circG.strokeCircle(orb.body.position.x, orb.body.position.y, r);
            }
            // 🟣 Always keep the glass in sync with the orb position/frame
            if (orb._glass) redrawOrbGlass(this, orb, ORB_GLASS);
          }
        }
      }
    }

    const BASE_W = deviceType === "mobile" ? "100%" : 1480;
    const BASE_H = deviceType === "mobile" ? "100%" : 720;

    game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: containerRef.current,
      backgroundColor: DEFAULT_WORLD_BACKGROUND_COLOR,
      render: {
        antialias: true,
        roundPixels: true, // helps reduce shimmer/soft edges when moving
      },
      scale: {
        mode: Phaser.Scale.FIT, // ✅ keep aspect ratio
        autoCenter: Phaser.Scale.CENTER_BOTH, // ✅ keep centered
        width: BASE_W,
        height: BASE_H,
      },
      pixelArt: true,
      physics: {
        default: "matter",
        matter: {
          debug: false,
          gravity: { y: 0 },
        },
      },
      scene: [
        StoryWorldBootScene,
        VoidCity2Scene,
        VillageScene,
        ArenaScene,
        VoidCityMerchant,
        DecksmithScene,
        GameHallScene,
        QuestHallScene,
        Route333Scene,
        WhisperingForestScene,
        NorthDocksScene,
        VoidCaveScene,
        VoidCaveDepthsScene,
        WestPassageScene,
        WestGrottoScene,
        RuneHollowScene,
        DeepGalleryScene,
        EastPassageScene,
        CrystalBasinScene,
        EchoVaultScene,
        ShatteredReachScene,
      ],
    });

    sceneRef.current = game;

    const emitPlayersToScenes = () => {
      const selfId = userData.id;
      const sceneManager = game.scene;
      [
        "VillageScene",
        "ArenaScene",
        "MerchantScene",
        "DecksmithScene",
        "GameHallScene",
        "QuestHallScene",
        "Route333Scene",
        "WhisperingForestScene",
        "NorthDocksScene",
        "VoidCity2Scene",
      ].forEach((sceneKey) => {
        const scene = sceneManager?.keys?.[sceneKey];
        if (scene) {
          scene.events.emit("setPlayers", players, selfId);
        }
      });
    };

    setTimeout(emitPlayersToScenes, 0);
    setTimeout(emitPlayersToScenes, 250);

    const handleMove = (e) => {
      const { dx, dy } = e.detail;
      [
        "VillageScene",
        "MerchantScene",
        "DecksmithScene",
        "GameHallScene",
        "QuestHallScene",
        "ArenaScene",
        "Route333Scene",
        "WhisperingForestScene",
        "VoidCity2Scene",
        "NorthDocksScene",
        "VoidCaveScene",
        "VoidCaveDepthsScene",
        "WestPassageScene",
        "WestGrottoScene",
        "RuneHollowScene",
        "DeepGalleryScene",
        "EastPassageScene",
        "CrystalBasinScene",
        "EchoVaultScene",
        "ShatteredReachScene",
      ].forEach((key) => {
        if (game.scene.keys[key]) {
          game.scene.keys[key].joystick = { x: dx, y: dy };
        }
      });
    };

    const handleRelease = () => {
      [
        "VillageScene",
        "MerchantScene",
        "DecksmithScene",
        "GameHallScene",
        "QuestHallScene",
        "ArenaScene",
        "Route333Scene",
        "WhisperingForestScene",
        "VoidCity2Scene",
        "NorthDocksScene",
        "VoidCaveScene",
        "VoidCaveDepthsScene",
        "WestPassageScene",
        "WestGrottoScene",
        "RuneHollowScene",
        "DeepGalleryScene",
        "EastPassageScene",
        "CrystalBasinScene",
        "EchoVaultScene",
        "ShatteredReachScene",
      ].forEach((k) => {
        if (game.scene.keys[k]) {
          game.scene.keys[k].joystick = { x: 0, y: 0 };
        }
      });
    };

    // const handleRelease = () => {
    //   game.scene.keys.VillageScene.joystick = { x: 0, y: 0 };
    // };

    window.addEventListener("joystick-move", handleMove);
    window.addEventListener("joystick-release", handleRelease);

    return () => {
      game.destroy(true);
      window.removeEventListener("joystick-move", handleMove);
      window.removeEventListener("joystick-release", handleRelease);
    };
  }, [
    onLocalMove,
    userData?.avatar?.name,
    userData?.avatar?.image,
    userData?.orbAvatar?.tokenId,
    userData?.orbAvatar?.composedImage,
  ]);

  useEffect(() => {
    if (npcDialogue) {
      window.addEventListener("keydown", handleOptionKeyPress);
    } else {
      window.removeEventListener("keydown", handleOptionKeyPress);
    }

    return () => {
      window.removeEventListener("keydown", handleOptionKeyPress);
    };
  }, [npcDialogue]);

  useEffect(() => {
    setNpcDialogue(null); // Defensive reset when scene re-inits
  }, []);

  return (
    <>
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          // justifyContent: "center",
          background: "#000", // letterbox color
          overflow: "hidden",
        }}
      />
      <DialogHUD
        theme={Theme}
        open={dialogOpen && !isStoryCutsceneActive}
        script={dialogScript}
        onClose={() => {
          if (typeof window !== "undefined") {
            window.__npcEngaged = false;
          }
          setDialogOpen(false);
          setDialogScript([]);
        }}
      />
      {forestChestReward && (
        <CardRewardModal
          visible={forestChestModalVisible}
          reward={forestChestReward}
          onClose={() => {
            setForestChestModalVisible(false);
            setForestChestReward(null);
          }}
        />
      )}
    </>
  );
}
